(() => {
  if (customElements.get('bd-product-pdf')) return;
  const resources = new Map();
  const clean = value => String(value ?? '').replace(/[\u2013\u2014]/g, '-').replace(/[\u2033\u201c\u201d]/g, '"').replace(/[\u2018\u2019\u2032]/g, "'").replace(/\s+/g, ' ').trim();
  const labels = {
    width_in_: 'Width (in.)', length: 'Length', thickness: 'Thickness',
    total_thickness_mm: 'Total thickness (mm)', underpad_thickness_mm: 'Attached pad (mm)',
    wear_layer_thickness_mil: 'Wear layer (mil)', planks_tiles_per_box: 'Pieces per box',
    residential_warranty_in_years: 'Residential warranty', commercial_warranty_in_years: 'Commercial warranty',
    radiant_heat_compatible: 'Radiant heat', click_lock_type: 'Locking system',
    underpad_iic_rating: 'Underpad IIC rating', underpad_stc_rating: 'Underpad STC rating',
    waterproof_water_resistant: 'Water resistance'
  };
  function loadScript(url, test) {
    if (test()) return Promise.resolve();
    if (!resources.has(url)) resources.set(url, new Promise((resolve, reject) => {
      const s = document.createElement('script');
      const timeout = setTimeout(() => { s.remove(); resources.delete(url); reject(new Error('Script timeout')); }, 20000);
      s.src = url;
      s.onload = () => { clearTimeout(timeout); if (test()) resolve(); else { resources.delete(url); reject(new Error('Library unavailable')); } };
      s.onerror = () => { clearTimeout(timeout); s.remove(); resources.delete(url); reject(new Error('Library unavailable')); };
      document.head.append(s);
    }));
    return resources.get(url);
  }
  async function fetchOK(url, options = {}) {
    const response = await fetch(url, { ...options, signal: AbortSignal.timeout(20000) });
    if (!response.ok) throw new Error('Product sheet request failed');
    return response;
  }
  function valueText(raw) {
    let v;
    try { v = JSON.parse(raw); } catch { v = raw; }
    if (Array.isArray(v)) return v.map(x => typeof x === 'object' ? '' : clean(x)).filter(Boolean).join(', ');
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (v && typeof v === 'object') {
      if ('value' in v) return clean(v.value) + (v.unit ? ' ' + clean(v.unit) : '');
      return '';
    }
    return clean(v).replace(/^\[['"]?|['"]?\]$/g, '');
  }
  function readData(template) {
    const root = template.content.firstElementChild;
    const d = { ...root.dataset };
    d.price = clean(root.querySelector('[data-pricing] .price__current')?.textContent);
    d.unit = clean(root.querySelector('[data-pricing] .price__unit')?.textContent);
    d.was = clean(root.querySelector('[data-pricing] .bd-was s')?.textContent);
    d.discount = clean(root.querySelector('[data-pricing] .bd-off')?.textContent);
    d.description = clean(root.querySelector('[data-description]')?.textContent);
    const features = [...root.querySelectorAll('[data-feature]')].filter(x=>clean(x.textContent));
    d.features = features.map(x=>clean(x.textContent));
    d.featureIcons = features.map(x=>x.dataset.icon || '');
    d.gallery = [...root.querySelectorAll('[data-gallery]')].map(x => x.dataset.gallery);
    d.galleryAlt = [...root.querySelectorAll('[data-gallery]')].map(x => clean(x.dataset.alt));
    d.specs = [...root.querySelectorAll('[data-spec-group]')].map(x => ({
      group: x.dataset.specGroup, key: x.dataset.key,
      label: labels[x.dataset.key] || labels[x.dataset.key.replace(/_+$/,'')] || clean(x.dataset.key.replace(/_/g, ' ')).replace(/^./, s => s.toUpperCase()),
      value: valueText(x.dataset.value)
    })).filter(x => x.value && !/^gid:\/\//.test(x.value));
    d.specs = d.specs.filter((x,i,a) => a.findIndex(y => y.label.toLowerCase() === x.label.toLowerCase()) === i);
    d.specs.sort((a,b) => ({dimensions:0,warranty:1,details:2}[a.group] ?? 3) - ({dimensions:0,warranty:1,details:2}[b.group] ?? 3));
    d.documents = [...root.querySelectorAll('[data-document]')].map(x => ({title:clean(x.textContent),url:new URL(x.dataset.document,d.url).href})).filter(x=>/^https?:/.test(x.url));
    d.accessories = [...root.querySelectorAll('[data-accessory]')].map(x => ({ ...x.dataset }));
    for (const item of [d,...d.accessories,...d.documents]) {
      const url = new URL(item.url);
      if (url.hostname.endsWith('.shopifypreview.com') || url.hostname.endsWith('.myshopify.com')) url.hostname = 'www.builddirect.com';
      item.url = url.href;
    }
    return d;
  }
  async function imageData(url, max = 1100) {
    if (!url) return null;
    try {
      const blob = await (await fetchOK(new URL(url, location.href).href, { mode: 'cors' })).blob();
      const img = await createImageBitmap(blob);
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale); canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.close();
      return { data: canvas.toDataURL('image/jpeg', 0.86), width: canvas.width, height: canvas.height };
    } catch { return null; }
  }
  function drawPDF(doc, d, media, fonts, qr) {
    const C = { ink: '#142c39', muted: '#596873', blue: '#007db6', line: '#dce1e2', pale: '#f5f5f2', white: '#ffffff' };
    let family = 'helvetica';
    if (fonts?.normal && fonts?.bold) {
      for (const style of ['normal','bold']) {
        doc.addFileToVFS('Montserrat-' + style + '.ttf', fonts[style]);
        doc.addFont('Montserrat-' + style + '.ttf', 'Montserrat', style);
      }
      family = 'Montserrat';
    }
    const font = (size, bold = false, color = C.ink) => { doc.setFont(family, bold ? 'bold' : 'normal'); doc.setFontSize(size); doc.setTextColor(color); };
    const text = (value,x,y,size = 9,bold = false,color = C.ink) => { font(size,bold,color); doc.text(clean(value),x,y); };
    const rect = (x,y,w,h,color) => { doc.setFillColor(color); doc.rect(x,y,w,h,'F'); };
    const line = (x,y,w,color = C.line) => { doc.setDrawColor(color); doc.setLineWidth(0.5); doc.line(x,y,x+w,y); };
    const lines = (value,width,size,bold = false) => { font(size,bold); return doc.splitTextToSize(clean(value),width); };
    const paragraph = (value,x,y,width,size,bold = false,color = C.ink) => {
      const rows = lines(value,width,size,bold); font(size,bold,color);
      doc.text(rows,x,y,{ lineHeightFactor: 1.35 }); return y + (rows.length - 1) * size * 1.35 + size * 0.4;
    };
    const fit = (value,x,y,width,size,maxLines,bold = false,color = C.ink) => {
      let rows = lines(value,width,size,bold);
      if (rows.length > maxLines) {
        rows = rows.slice(0,maxLines);
        let last = rows[maxLines-1];
        while (last && lines(last + '...',width,size,bold).length > 1) last = last.slice(0,-1);
        rows[maxLines-1] = last.trim() + '...';
      }
      font(size,bold,color); doc.text(rows,x,y,{lineHeightFactor:1.3}); return y + rows.length * size * 1.3;
    };
    const photo = (img,x,y,w,h,cover = false) => {
      rect(x,y,w,h,C.white);
      if (!img) return;
      const r = cover ? Math.max(w/img.width,h/img.height) : Math.min(w/img.width,h/img.height);
      doc.saveGraphicsState();
      doc.rect(x,y,w,h,null); doc.clip(); doc.discardPath();
      doc.addImage(img.data,'JPEG',x+(w-img.width*r)/2,y+(h-img.height*r)/2,img.width*r,img.height*r,undefined,'FAST');
      doc.restoreGraphicsState();
    };
    doc.setProperties({ title: d.title + (d.style ? ' | ' + d.style : ''), subject: 'BuildDirect product sheet', author: 'BuildDirect', creator: 'BuildDirect', keywords: [d.title,d.style,d.sku].filter(Boolean).join(', ') });
    const border=(x,y,w,h)=>{doc.setDrawColor(C.line);doc.setLineWidth(0.5);doc.rect(x,y,w,h,'S');};
    const center=(value,x,y,w,size,bold=false,color=C.ink,maxLines=2)=>{
      const rows=lines(value,w,size,bold).slice(0,maxLines);font(size,bold,color);
      rows.forEach((row,i)=>doc.text(row,x+w/2,y+i*size*1.3,{align:'center'}));
    };
    const arrow=(x,y)=>{doc.setDrawColor(C.blue);doc.setLineWidth(0.8);doc.line(x,y+5,x+5,y);doc.line(x,y,x+5,y);doc.line(x+5,y,x+5,y+5);};
    const header=label=>{
      if(media.logo)photo(media.logo,32,22,145,25);else text('BuildDirect',32,43,21,true);
      font(8,true,C.muted);doc.text(label,580,29,{align:'right'});
      font(7.2,false,C.muted);doc.text('Better floors. Expert help. Delivered.',580,45,{align:'right'});
      line(32,57,548);
    };
    const selected=d.style || d.title;
    const hasStyle=Boolean(d.style);
    const identity=()=>{
      text('Product specifications',32,99,24,true);
      fit(selected,32,121,300,10,1,true);
      font(8,false,C.muted);doc.text('SKU '+d.sku,580,121,{align:'right'});
    };
    const coverage=Number(d.coverage)>0 && d.measure && d.sellingUnit ? {key:'coverage',group:'dimensions',label:'Coverage per '+d.sellingUnit,value:Number(d.coverage).toLocaleString('en-US',{maximumFractionDigits:2})+' '+d.measure}:null;
    const dimension=(prefix,label)=>{const row=d.specs.find(s=>(s.key || '').startsWith(prefix));if(!row)return null;return {...row,label,value:prefix==='width' && /in/.test(row.key) && !/["′″a-z]/i.test(row.value)?row.value+'"':row.value};};
    const facts=[dimension('width','Width'),dimension('length','Length'),dimension('thickness','Thickness'),coverage].filter(Boolean);
    const technical=/flooring|hardwood|laminate|vinyl|deck|carpet|tile/i.test(d.type) && !/molding|trim|adhesive|tool|underlay/i.test(d.type) || d.specs.length>5 || d.documents.length>0;
    header('PRODUCT OVERVIEW');
    fit(d.type.toUpperCase(),32,81,360,8,1,true,C.blue);
    let styleSize=30;while(lines(selected,365,styleSize,true).length>1 && styleSize>20)styleSize--;
    fit(selected,32,115,365,styleSize,1,true);
    if(hasStyle)fit(d.title,32,139,310,10.8,2);
    fit('SKU '+d.sku,32,166,365,7.8,1,true,C.muted);
    rect(420,74,160,98,C.ink);
    text('PRODUCT PRICE',434,91,6.5,true,'#c8dde5');
    let priceSize=29;font(priceSize,true);while(doc.getTextWidth(d.price)>103 && priceSize>14)font(--priceSize,true);
    text(d.price || 'View online',434,126,priceSize,true,C.white);
    font(7,false,C.white);doc.text(d.unit,566,126,{align:'right'});
    if(d.was){
      text('Was '+d.was,434,154,7.5,false,'#c8dde5');font(7.5);
      line(434+doc.getTextWidth('Was '),151.5,doc.getTextWidth(d.was),'#c8dde5');
      if(d.discount){rect(519,145,47,16,C.blue);center(d.discount,519,156,47,6.5,true,C.white,1);}
    }
    const imageKey=url=>{try{return new URL(url,'https://www.builddirect.com').pathname;}catch{return url;}};
    const unique=[],seen=new Set();
    const addPhoto=(img,url,alt='')=>{if(img && !seen.has(imageKey(url))){seen.add(imageKey(url));unique.push({img,url,alt});}};
    d.gallery.forEach((url,i)=>addPhoto(media.gallery[i],url,d.galleryAlt?.[i]));addPhoto(media.hero,d.image);
    const material=/floor|hardwood|laminate|vinyl|deck|carpet|tile/i.test(d.type) && !/molding|trim|adhesive|tool|underlay/i.test(d.type);
    const preferred=unique.find(p=>/room|scene|lifestyle|installed/i.test(p.alt)) || unique.find(p=>/-01(?:[._-]|%)/i.test(p.url));
    const lead=material ? preferred || unique[0] : unique.find(p=>imageKey(p.url)===imageKey(d.image)) || unique[0];
    const detail=unique.find(p=>p!==lead && imageKey(p.url)===imageKey(d.image)) || unique.find(p=>p!==lead);
    const supporting=[detail,...unique.filter(p=>p!==lead && p!==detail)].filter(Boolean).slice(0,4);
    if(lead)photo(lead.img,32,184,supporting.length ? 268 : 548,268,false);
    supporting.forEach((p,i)=>{
      const single=supporting.length===1;
      photo(p.img,312+(i%2)*140,184+Math.floor(i/2)*140,single?268:128,single?268:128,false);
    });
    if(facts.length){
      rect(32,465,548,50,C.pale);const w=548/facts.length;
      facts.forEach((s,i)=>{
        const x=32+i*w;
        if(i){doc.setDrawColor(C.line);doc.line(x,476,x,504);}
        fit(s.value,x+15,486,w-26,15,1,true);
        fit(s.label.toUpperCase(),x+15,503,w-26,6.2,1,true,C.muted);
      });
    }
    const features=d.features.slice(0,6);
    if(technical && features.length){
      const w=548/features.length;
      features.forEach((f,i)=>{
        const x=32+i*w;
        if(media.features?.[i])photo(media.features[i],x+w/2-12,528,24,24);
        center(f,x+5,565,w-10,7.8,true,C.ink,3);
      });
    }else if(technical && d.description)fit(d.description,32,539,548,9,4,false,C.muted);
    const accessories=d.accessories.slice(0,4);
    if(technical && accessories.length){
      line(32,591,548);text('Complete your project',32,612,12,true);
      font(6.7,false,C.muted);doc.text('Explore coordinating accessories',580,611,{align:'right'});
      accessories.forEach((a,i)=>{
        const x=32+(i%2)*280,y=626+Math.floor(i/2)*43;
        rect(x,y,268,35,'#f4f6f6');photo(media.accessories[i],x+7,y+4,44,27);
        fit(a.style || a.title,x+60,y+13,182,8.7,1,true);
        fit(a.cardPrice+(a.cardUnit?' / '+a.cardUnit:''),x+60,y+27,182,7.5,1,false,C.muted);
        arrow(x+253,y+14);doc.link(x,y,268,35,{url:a.url});
      });
    }else if(!technical){
      text('Product details',32,540,12,true);let sy=560;
      d.specs.forEach(row=>{text(row.label,32,sy,8,false,C.muted);sy=paragraph(row.value,250,sy,330,8.5,true)+12;});
    }
    if(technical){
      const aliases={width_in_:'Width',length:'Length',total_thickness_mm_:'Total thickness',underpad_thickness_mm_:'Attached pad',wear_layer_thickness_mil_:'Wear layer',underlay_recommended_for_installation:'Installation underlay',underpad_iic_rating:'Underpad IIC rating',underpad_stc_rating:'Underpad STC rating'};
      const specs=d.specs.map(s=>({...s,label:aliases[s.key] || s.label}));
      specs.forEach(s=>{
        if(/^(width_in_|total_thickness_mm_|underpad_thickness_mm_|wear_layer_thickness_mil_)$/.test(s.key) && /^[\d.]+$/.test(s.value))s.value+=' '+(s.key==='width_in_'?'in.':s.key==='wear_layer_thickness_mil_'?'mil':'mm');
      });
      const total=specs.find(s=>/^total_thickness_mm/.test(s.key));
      const filtered=specs.filter(s=>!(s.key==='thickness' && total && s.value.toLowerCase()===total.value.toLowerCase()));
      if(coverage && !filtered.some(s=>/coverage/i.test(s.key)))filtered.push(coverage);
      const groupFor=s=>{
        const k=s.key || s.label;
        if(s.group==='warranty' || /certif|waterproof|water_resistan|iic|stc|slip|fire_rating|abrasion/.test(k))return 3;
        if(/install|click|grade|radiant|underlay|underpad_type|underpad_attached|core|construction/.test(k))return 2;
        if(s.group==='dimensions' || /per_box|weight|coverage/.test(k))return 0;
        return 1;
      };
      const groups=['Dimensions & packaging','Appearance & finish','Construction & installation','Performance & warranty'].map((name,index)=>({name,rows:filtered.filter(s=>groupFor(s)===index)}));
      const dimOrder=['width','length','total_thickness','thickness','underpad_thickness','wear_layer','planks_tiles','coverage'];
      groups[0].rows.sort((a,b)=>{const rank=s=>{const i=dimOrder.findIndex(k=>(s.key || '').startsWith(k));return i<0?99:i;};return rank(a)-rank(b);});
      const documents=d.documents || [];
      const shortDocument=title=>clean(title).replace(/^Vinyl Flooring\s*-\s*/i,'').replace(/^Planning Your Floor Layout Near Moldings$/i,'Floor layout near moldings').replace(/^Moldings\s*-\s*/i,'Moldings: ').replace(/\s*-\s*Technical Specification$/i,': technical specification');
      const rowHeight=row=>Math.max(lines(row.label,103,8.1).length,lines(row.value,140,8.5,true).length)*11+9;
      const panelHeight=group=>28+group.rows.reduce((h,row)=>h+rowHeight(row),0)+10;
      let remaining=groups.map(g=>({...g,rows:[...g.rows]}));
      let pageIndex=0;
      do{
        doc.addPage();header('TECHNICAL DETAILS');identity();
        const panelTop=139,panelGap=14,panelMax=213;
        const chunks=remaining.map(g=>{
          const rows=[];let h=38;
          while(g.rows.length && h+rowHeight(g.rows[0])<=panelMax){const row=g.rows.shift();rows.push(row);h+=rowHeight(row);}
          if(!rows.length && g.rows.length){
            const row=g.rows.shift(),capacity=12;
            const valueLines=lines(row.value,140,8.5,true);
            rows.push({...row,value:valueLines.slice(0,capacity).join(' ')});
            if(valueLines.length>capacity)g.rows.unshift({...row,label:row.label+' (continued)',value:valueLines.slice(capacity).join(' ')});
          }
          return {name:g.name+(pageIndex?' (continued)':''),rows};
        });
        const topH=Math.max(...chunks.slice(0,2).map(panelHeight));
        const bottomH=Math.max(...chunks.slice(2).map(panelHeight));
        chunks.forEach((group,index)=>{
          if(!group.rows.length)return;
          const x=32+(index%2)*280,y=index<2?panelTop:panelTop+topH+panelGap;
          const h=index<2?topH:bottomH;
          border(x,y,268,h);rect(x,y,268,28,'#f4f6f6');text(group.name,x+11,y+18,9.5,true);
          let sy=y+39;
          const warranties=group.rows.filter(row=>row.group==='warranty').sort((a,b)=>Number(/commercial/.test(a.key))-Number(/commercial/.test(b.key)));
          const useCallout=index===3 && warranties.length>0 && warranties.length<=2 && warranties.every(row=>lines(row.value,110,15,true).length===1);
          const rows=useCallout?group.rows.filter(row=>row.group!=='warranty'):group.rows;
          rows.forEach((row,i)=>{
            const rh=rowHeight(row);
            paragraph(row.label,x+11,sy,103,8.1,false,C.muted);paragraph(row.value,x+121,sy,136,8.5,true);
            if(i<rows.length-1)line(x+11,sy+rh-10,246);
            sy+=rh;
          });
          if(useCallout){
            const cy=Math.max(sy+1,y+h-66),w=246/warranties.length;
            rect(x+11,cy,246,56,'#edf6fa');
            warranties.forEach((row,i)=>{
              fit(row.label,x+21+i*w,cy+16,w-18,7,1,false,C.muted);
              fit(row.value,x+21+i*w,cy+43,w-18,15,1,true);
            });
          }
        });
        pageIndex++;
      }while(remaining.some(g=>g.rows.length));
      if(documents.length){
        let y=589;
        text('Product documents',32,y,12,true);
        font(7,false,C.muted);doc.text('Click to open the original PDF',580,y,{align:'right'});line(32,y+13,548);y+=30;
        for(let i=0;i<documents.length;i+=2){
          const pair=documents.slice(i,i+2),height=Math.max(...pair.map(r=>lines(shortDocument(r.title),250,7.5).length*10+11));
          if(y+height>706){doc.addPage();header('PRODUCT DOCUMENTS');identity();y=151;}
          pair.forEach((r,j)=>{
            const x=32+j*280;paragraph(shortDocument(r.title),x,y,250,7.5,false,C.blue);
            arrow(x+260,y-5);line(x,y+height-10,268);doc.link(x,y-8,268,height,{url:r.url});
          });y+=height;
        }
      }
    }
    const pages=doc.getNumberOfPages();
    for(let n=1;n<=pages;n++){
      doc.setPage(n);
      if(n===1){
        rect(32,718,548,44,C.ink);text('Let’s get your project moving.',44,735,10,true,C.white);
        text('Talk to a flooring expert  |  1-877-631-2845',44,752,7.5,false,C.white);
        doc.link(44,740,293,17,{url:'tel:+18776312845'});
        text('View product',464,735,8,true,C.white);text('builddirect.com',458,752,7,false,C.white);
        if(qr){rect(534,722,36,36,C.white);doc.addImage(qr,'PNG',537,725,30,30);}
        doc.link(450,721,120,39,{url:d.url});
      }else{
        rect(32,726,548,34,C.ink);text('Need help with your project?',44,747,9,true,C.white);
        text('1-877-631-2845',341,747,9,true,C.white);text('builddirect.com',491,747,8,false,C.white);
        doc.link(335,732,130,24,{url:'tel:+18776312845'});doc.link(484,732,89,24,{url:d.url});
      }
      text('Prepared '+d.prepared+'. Prices and availability may change. Shipping and taxes excluded.',32,779,6.2,false,C.muted);
      font(7,true,C.muted);doc.text(String(n).padStart(2,'0')+' / '+String(pages).padStart(2,'0'),580,779,{align:'right'});
    }
  }

  class ProductPDF extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.clickHandler = e => { if (e.target.closest('button')) this.download(); };
      this.addEventListener('click',this.clickHandler);
      this.variantChanged = () => {
        const link = this.querySelector('[data-pdf-download]');
        if (link) link.hidden = true;
        if (this.pdfURL) { URL.revokeObjectURL(this.pdfURL); this.pdfURL = null; }
      };
      document.addEventListener('on:variant:change',this.variantChanged);
    }
    disconnectedCallback() {
      if (this.pdfURL) URL.revokeObjectURL(this.pdfURL);
      document.removeEventListener('on:variant:change',this.variantChanged);
      this.removeEventListener('click',this.clickHandler);
      this.bound = false;
    }
    selectedVariant() {
      const product = document.querySelector('.product[data-section]');
      const input = product?.querySelector('form[action*="/cart/add"] [name="id"]');
      return input?.value || new URL(location.href).searchParams.get('variant') || this.dataset.variantId;
    }
    async download() {
      if (this.busy) return;
      this.busy = true;
      const button = this.querySelector('button'), status = this.querySelector('[role="status"]');
      const label = button.textContent;
      button.disabled = true; button.setAttribute('aria-busy','true');
      button.textContent = this.dataset.loading; status.textContent = this.dataset.loading;
      try {
        const id = this.selectedVariant();
        const url = new URL(this.dataset.productUrl,location.origin);
        url.searchParams.set('variant',id); url.searchParams.set('section_id','bd-product-pdf-data');
        const [response] = await Promise.all([
          fetchOK(url.href,{credentials:'same-origin',cache:'no-store'}),
          loadScript('https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js',() => window.jspdf?.jsPDF),
          loadScript('https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js',() => window.QRCode).catch(() => {})
        ]);
        const html = new DOMParser().parseFromString(await response.text(),'text/html');
        const template = html.querySelector('template[data-bd-pdf-data]');
        if (!template) throw new Error('Product data unavailable');
        const d = readData(template);
        if (d.variantId !== String(id) || d.productId !== this.dataset.productId) throw new Error('Variant changed');
        const fontUrl = this.dataset.fontUrl;
        if (!resources.has(fontUrl)) resources.set(fontUrl,fetchOK(fontUrl).then(r=>r.json()).catch(() => { resources.delete(fontUrl); return null; }));
        const [fonts,hero,logo,gallery,accessories,features] = await Promise.all([
          resources.get(fontUrl),imageData(d.image),imageData(d.logo,700),
          Promise.all(d.gallery.map(u=>imageData(u,1100))),
          Promise.all(d.accessories.map(a=>imageData(a.image,250))),
          Promise.all(d.featureIcons.map(u=>imageData(u,160)))
        ]);
        let qr = null;
        if (window.QRCode) {
          const target = document.createElement('div');
          new QRCode(target,{text:d.url,width:180,height:180,correctLevel:QRCode.CorrectLevel.M});
          qr = target.querySelector('canvas')?.toDataURL('image/png');
        }
        const doc = new window.jspdf.jsPDF({orientation:'portrait',unit:'pt',format:'letter',compress:true,putOnlyUsedFonts:true});
        drawPDF(doc,d,{hero,logo,gallery,accessories,features},fonts,qr);
        const name = clean(d.title+'-'+(d.sku || d.variantId)).replace(/[<>:"/\\|?*\x00-\x1f]/g,'-').slice(0,160);
        if (this.pdfURL) URL.revokeObjectURL(this.pdfURL);
        this.pdfURL = URL.createObjectURL(doc.output('blob'));
        const link = this.querySelector('[data-pdf-download]');
        link.href = this.pdfURL; link.download = name+'.pdf'; link.hidden = false;
        link.click();
        status.textContent = this.dataset.ready;
      } catch (error) {
        console.error('BuildDirect product PDF:',error);
        status.textContent = this.dataset.error;
        alert(this.dataset.error);
      } finally {
        button.disabled=false; button.removeAttribute('aria-busy'); button.textContent=label; this.busy=false;
      }
    }
  }
  customElements.define('bd-product-pdf',ProductPDF);
})();
